/** @jsx jsx */
/** @jsxRuntime classic */

import { Interpolation, jsx } from '@emotion/react';
import React, { useMemo } from 'react';
import { ThemeConfig } from '../style/theme';
import ButtonGroupContext from './ButtonGroupContext';

export type Props = React.PropsWithChildren<{
  orientation?: 'vertical' | 'horizontal';
  fullWidth?: boolean;
  grouped?: boolean;
  color?: 'primary' | 'neutral';
  weight?: 'normal' | 'bold';
  disabled?: boolean;
  role?: React.AriaRole;
}>;

const ButtonGroup: React.FC<Props> = ({
  children,
  orientation = 'horizontal',
  fullWidth = true,
  grouped = true,
  color,
  weight,
  disabled,
  role = 'group',
}) => {
  const context = useMemo(
    () => ({
      color,
      weight,
      disabled,
    }),
    [color, weight, disabled]
  );

  const css: Interpolation<ThemeConfig> = {
    display: 'inline-flex',
    boxSizing: 'border-box',
    padding: '1px',
    ...(orientation === 'horizontal' && {
      flexDirection: 'row',
    }),
    ...(orientation === 'vertical' && {
      flexDirection: 'column',
    }),
    ...(fullWidth && {
      width: '100%',
      boxSizing: 'border-box',
    }),
    '& > .origami-Button': {
      minWidth: '10px',
      margin: 0,
      '&:focus': {
        boxShadow: 'none',
      },
      ...(!fullWidth && {
        width: 'fit-content',
      }),
      ...(grouped &&
        orientation === 'vertical' && {
          '&:not(:first-of-type)': {
            marginTop: '-1px',
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          },
          '&:not(:last-of-type)': {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        }),
      ...(grouped &&
        orientation === 'horizontal' && {
          '&:not(:first-of-type)': {
            marginLeft: '-1px',
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
          },
          '&:not(:last-of-type)': {
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          },
        }),
    },
  };

  return (
    <div
      style={{ display: 'inline-flex' }}
      css={css}
      className="origami-ButtonGroup"
      role={role}
    >
      <ButtonGroupContext.Provider value={context}>
        {children}
      </ButtonGroupContext.Provider>
    </div>
  );
};

export default ButtonGroup;
